import { useState, useMemo } from "react";
import { Sparkles, Quote, Calendar, User, RefreshCw, Flower2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { getThoughtOfTheDay, thoughtsPool } from "../../mock/thoughts";

const ThoughtOfTheDay = () => {
  const [seed, setSeed] = useState(0); // For "show another" button
  const [activeTab, setActiveTab] = useState("sanskrit");

  const thought = useMemo(() => {
    if (seed === 0) return getThoughtOfTheDay();
    // Rotate to next thought if user clicks refresh
    const today = getThoughtOfTheDay();
    const currentIdx = thoughtsPool.findIndex(
      (t) => t.sanskrit === today.sanskrit && t.author === today.author
    );
    const nextIdx = (currentIdx + seed + thoughtsPool.length) % thoughtsPool.length;
    return { ...thoughtsPool[nextIdx], dayOfYear: today.dayOfYear };
  }, [seed]);

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  return (
    <section className="py-20 relative overflow-hidden bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 text-white">
      {/* Decorative mandala pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 border-4 border-yellow-300 rounded-full" />
        <div className="absolute top-20 left-20 w-52 h-52 border-4 border-yellow-300 rounded-full" />
        <div className="absolute top-32 left-32 w-32 h-32 border-4 border-yellow-300 rounded-full" />
        <div className="absolute bottom-10 right-10 w-80 h-80 border-4 border-yellow-300 rounded-full" />
        <div className="absolute bottom-20 right-20 w-56 h-56 border-4 border-yellow-300 rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-yellow-300/30 mb-4">
            <Flower2 className="w-4 h-4 text-yellow-300" />
            <span className="text-xs font-semibold text-yellow-200 tracking-widest uppercase">Thought of the Day</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
            आज का <span className="text-yellow-300">प्रेरणा वचन</span>
          </h2>
          <div className="flex items-center justify-center gap-2 text-orange-100 text-sm">
            <Calendar className="w-4 h-4" />
            <span>{today}</span>
            <span className="opacity-60">·</span>
            <span className="opacity-80">Day {thought.dayOfYear} of the year</span>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-md rounded-3xl border border-yellow-300/20 p-8 md:p-10 shadow-2xl">
            {/* Category badge */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                {thought.category}
              </span>
              <button
                onClick={() => setSeed((s) => s + 1)}
                className="inline-flex items-center gap-1.5 text-xs text-yellow-300/80 hover:text-yellow-300 transition-colors"
                aria-label="Show another thought"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Another
              </button>
            </div>

            {/* Language tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-3 bg-white/5 border border-yellow-300/20 mb-6 h-auto p-1">
                <TabsTrigger value="sanskrit" className="data-[state=active]:bg-yellow-300 data-[state=active]:text-orange-900 text-yellow-200/70 py-2 text-xs md:text-sm rounded-lg font-semibold">
                  संस्कृत
                </TabsTrigger>
                <TabsTrigger value="hindi" className="data-[state=active]:bg-yellow-300 data-[state=active]:text-orange-900 text-yellow-200/70 py-2 text-xs md:text-sm rounded-lg font-semibold">
                  हिन्दी
                </TabsTrigger>
                <TabsTrigger value="english" className="data-[state=active]:bg-yellow-300 data-[state=active]:text-orange-900 text-yellow-200/70 py-2 text-xs md:text-sm rounded-lg font-semibold">
                  English
                </TabsTrigger>
              </TabsList>

              <div className="relative min-h-[160px]">
                <Quote className="absolute -top-2 -left-1 w-10 h-10 text-yellow-300/20" />

                <TabsContent value="sanskrit" className="mt-0">
                  <p className="font-devanagari text-2xl md:text-3xl leading-relaxed text-yellow-100 pl-10 pr-4 text-center">
                    {thought.sanskrit}
                  </p>
                </TabsContent>

                <TabsContent value="hindi" className="mt-0">
                  <p className="font-devanagari text-xl md:text-2xl leading-relaxed text-yellow-100 pl-10 pr-4 text-center">
                    {thought.hindi}
                  </p>
                </TabsContent>

                <TabsContent value="english" className="mt-0">
                  <p className="text-xl md:text-2xl leading-relaxed text-yellow-50 italic pl-10 pr-4 text-center font-display">
                    "{thought.english}"
                  </p>
                </TabsContent>
              </div>
            </Tabs>

            {/* Author */}
            <div className="mt-8 pt-6 border-t border-yellow-300/20 flex items-center justify-center gap-2 text-sm text-yellow-200/90">
              <User className="w-4 h-4" />
              <span className="font-semibold">— {thought.author}</span>
            </div>
          </div>

          {/* Info line */}
          <p className="text-center text-xs text-orange-100/70 mt-6">
            A new thought is chosen daily from a collection of {thoughtsPool.length}+ curated Indian wisdom.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ThoughtOfTheDay;
